'use client'

import classNames from "classnames";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import slug from "slug";

import { getSuggestions } from "@/actions/search";
import { event } from "@/lib/analytics/gtag";
import { Button, Input } from "@ui/primitives";

interface SearchFormData {
    term: string;
}

interface SearchSuggestion {
    id: string;
    title: string;
    type: 'tablature' | 'artist' | 'category';
    url: string;
}

interface TablatureSearchFormProps {
    dictionary: Record<string, any>;
}

export const TablatureSearchForm = ({ dictionary }: TablatureSearchFormProps) => {
    const { register, formState: { errors }, handleSubmit, watch, setValue } = useForm<SearchFormData>();
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const router = useRouter();

    const getNestedValue = (obj: any, path: string): string => {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj) || path;
    };

    const t = (key: string) => getNestedValue(dictionary, key);
    const ts = t;

    const searchTerm = watch('term', '');

    // Fetch suggestions from backend
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!searchTerm || searchTerm.length < 2) {
                setSuggestions([]);
                return;
            }

            try {
                const results = await getSuggestions(searchTerm);
                setSuggestions(results);
            } catch (error) {
                console.error('Failed to fetch suggestions:', error);
                setSuggestions([]);
            }
        };

        const timeoutId = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleSearch = useCallback(async (data: SearchFormData) => {
        if (!data.term?.trim()) return;

        setIsLoading(true);
        setShowSuggestions(false);

        try {
            const searchQuery = slug(data.term.trim(), { lower: true });

            event({
                action: 'search',
                category: 'tablatures',
                label: 'omnisearch',
                search_term: searchQuery
            });

            router.push(`/tablatures?search=${encodeURIComponent(searchQuery)}`);
        } catch (error) {
            console.error('Search navigation failed:', error);
        } finally {
            setIsLoading(false);
        }
    }, [router]);

    const handleSuggestionClick = useCallback((suggestion: SearchSuggestion) => {
        event({
            action: 'search_suggestion_click',
            category: 'tablatures',
            label: suggestion.type,
            value: suggestion.id
        });

        setValue('term', suggestion.title);
        setShowSuggestions(false);
        router.push(`/tablatures?search=${encodeURIComponent(suggestion.title)}`);

    }, [router, setValue]);

    const handleInputFocus = useCallback(() => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    }, [suggestions.length]);

    const handleInputBlur = useCallback(() => {
        setTimeout(() => setShowSuggestions(false), 200);
    }, []);

    const getSuggestionIcon = (type: SearchSuggestion['type']) => {
        switch (type) {
            case 'tablature': return 'fa-solid fa-music';
            case 'artist': return 'fa-solid fa-user';
            case 'category': return 'fa-solid fa-tag';
            default: return 'fa-solid fa-search';
        }
    };

    const getSuggestionTypeLabel = (type: SearchSuggestion['type']) => {
        switch (type) {
            case 'tablature': return t('generic.tablature');
            case 'artist': return t('generic.artist');
            case 'category': return t('generic.category');
            default: return '';
        }
    };

    return (
        <div className="relative w-full max-w-md">
            <form
                onSubmit={handleSubmit(handleSearch)}
                className="flex"
                role="search"
                aria-label={t('menu.search_aria_label')}
            >
                <div className="relative flex-1">
                    <Input
                        {...register('term', {
                            // required: ts('forms.validation.required'),
                            // minLength: {
                            //     value: 2,
                            //     message: ts('forms.validation.min_length', { count: 2 })
                            // }
                        })}
                        placeholder={ts('menu.search_placeholder')}
                        className={classNames(
                            "w-full border-primary-600 rounded-l-lg bg-white rounded-r-none",
                            "focus:border-primary-600 focus:ring-primary-600",
                            { 'border-error-500': errors.term }
                        )}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        disabled={isLoading}
                        aria-describedby={errors.term ? 'search-error' : undefined}
                        aria-expanded={showSuggestions}
                        aria-autocomplete="list"
                        autoComplete="off"
                    />

                    {errors.term && (
                        <div
                            id="search-error"
                            className="absolute top-full left-0 mt-1 text-xs text-error-500"
                            role="alert"
                        >
                            {errors.term.message as string}
                        </div>
                    )}
                </div>

                <Button
                    type="submit"
                    variant="reverse"
                    icon={<i className="fas fa-search" />}
                    loading={isLoading}
                    className="border-l-0 rounded-l-none"
                    disabled={isLoading || !searchTerm?.trim()} />
            </form>

            {/* Search suggestions dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-100 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                    <div className="p-2">
                        <div className="text-xs text-gray-600 mb-2 px-2">
                            {t('menu.search_suggestions')}
                        </div>
                        {suggestions.map((suggestion) => (
                            <button
                                key={suggestion.id}
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-secondary-400 rounded-md transition-colors"
                                role="option"
                            >
                                <div className="flex-shrink-0 text-gray-500">
                                    <i className={getSuggestionIcon(suggestion.type)} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                        {suggestion.title}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {getSuggestionTypeLabel(suggestion.type)}
                                    </div>
                                </div>
                                <div className="flex-shrink-0 text-gray-400">
                                    <i className="fa-solid fa-arrow-right text-xs" />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
